import { QUERY_KEYS, SERVER_ROUTES } from "@constants/api.routes";
import { api } from "@config/axios/axios.config";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { HTTP_STATUS } from "@constants/http.status.code";
import { showNotification } from "@libs/notistack/notificationStack";
import { SNACKBAR_SEVERITY } from "@constants/notificationStack";
import type { ProfileImageUpdateResponse } from "@features/Profile/types";

export const useUpdateProfileImage = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (file: File): Promise<ProfileImageUpdateResponse> => {
			// 1. Get presigned URL and objectKey
			const uploadUrlRes = await api.post(SERVER_ROUTES.USER.PROFILE_IMAGE_UPLOAD_URL, {
				originalName: file.name,
				contentType: file.type,
			});
			const { uploadUrl, objectKey } = uploadUrlRes.data;

			// 2. Upload the image to the presigned URL
			await fetch(uploadUrl, {
				method: "PUT",
				headers: {
					"Content-Type": file.type,
				},
				body: file,
			});

			// 3. Notify backend of the new image
			const updateRes = await api.patch<ProfileImageUpdateResponse>(SERVER_ROUTES.USER.PROFILE_IMAGE_UPDATE, {
				objectKey,
			});
			return updateRes.data;
		},
		onSuccess: (response: ProfileImageUpdateResponse) => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER.GET_PROFILE] });
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ATTACHMENTS.ATTACHMENT_BY_KEY] });
			if (response?.statusCode === HTTP_STATUS.OK || response?.statusCode === HTTP_STATUS.CREATED) {
				if (response?.message) {
					showNotification({
						message: response?.message,
						severity: SNACKBAR_SEVERITY.SUCCESS,
					});
				}
			}
		},
	});
};
